import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Property } from '../models/property';
import { AuthenticationService } from './AuthenticationService';
import { PropertyDeleteRequest } from '../requests/propertyDeleteRequest';
import { UserPropertyDTO } from '../DTO/userPropertyDTO';
import { AppConfig } from '../app.config';
import { UpdateRequest } from '../requests/updateRequest';
import { BuyerRepresentationRequest } from '../requests/buyerRepresentationRequest';
import { LegalAdRequest } from '../requests/legalAdRequest';
import { PropertyImagePoolDTO } from '../DTO/propertyImagePoolDTO';
import { TrashItemKind } from '../models/propertyTrash';
import { LegalAdVoidRequest } from '../requests/legalAdVoidRequest';
import { PropertyAttachmentViewRequest } from '../requests/propertyAttachmentViewRequest';
import { SaleType } from '../models/saleType';
import { BasicResponse } from '../responses/basicResponse';
import { InventoryResponse } from '../responses/inventoryResponse';
import { CreateLegalAdRequest } from '../requests/createLegalAdRequest';
import { SampleFileRequest } from '../requests/sampleFileRequest';
import { Inventory } from '../models/inventory';
import { StoreInventoryRequest } from '../requests/storeInventoryRequest';
import { DeleteInventoryRequest } from '../requests/deleteInventoryRequest';
import { StoreInventoryBulkRequest } from '../requests/storeInventoryBulkRequest';
import { VaultDocument } from '../models/vaultDocument';
import { StoreVaultDocumentRequest } from '../requests/storeVaultDocumentRequest';
import { ExecuteLegalDocumentRequest } from '../requests/executeLegalDocumentRequest';
import { DraftLegalDocumentRequest } from '../requests/draftLegalDocumentRequest';
import { DeleteVaultDocumentRequest } from '../requests/deleteVaultDocumentRequest';
import { StoreInventoryImagesRequest } from '../requests/storeInventoryImagesRequest';
import { InventoryImage } from '../models/inventoryImage';
import { UserAuctionAuthorizationStatusRequest } from '../requests/userAuctionAuthorizationStatusRequest';
import { PropertyNote } from '../models/propertyNote';
import { DeletePropertyNoteRequest } from '../requests/deletePropertyNoteRequest';
import { StorePropertyNoteRequest } from '../requests/storePropertyNoteRequest';
import { PropertyExpense } from '../models/propertyExpense';
import { StorePropertyExpenseRequest } from '../requests/storePropertyExpenseRequest';
import { DeletePropertyExpenseRequest } from '../requests/deletePropertyExpenseRequest';
import { HotEditRequest } from '../requests/hotEditRequest';
import { ReserveCreditRequest } from '../requests/reserveCreditRequest';
import { LoggingService } from './LoggingService';
import { FolderStructure } from '../models/folderStructure';
import { StoreFolderStructureRequest } from '../requests/storeFolderStructureRequest';
import { UpdateVaultDocumentFolderPathRequest } from '../requests/updateVaultDocumentFolderPathRequest';
import { StorePortfolioRequest } from '../requests/storePortfolioRequest';
import { Portfolio } from '../models/portfolio';
import { DeletePortfolioRequest } from '../requests/deletePortfolioRequest';
import { StorePortfolioPropertyRequest } from '../requests/storePortfolioPropertyRequest';
import { PropertyParty } from '../models/propertyParty';
import { PropertyMasterSettings } from '../models/propertyMasterSettings';
import { PropertyTask } from '../models/propertyTask';
import { PropertyPreservation } from '../models/propertyPreservation';
import { PropertyManagement } from '../models/propertyManagement';
import { UnlockPropertyRequest } from '../requests/unlockPropertyRequest';
import { UpdatePropertyPhaseRequest } from '../requests/updatePropertyPhaseRequest';
import { DuplicateAddressCheckRequest } from '../requests/duplicateAddressCheckRequest';
import { request } from 'https';

@Injectable({
  providedIn: 'root'
})
export class PropertyDataApiService {

  constructor(private httpClient: HttpClient, private appConfig:AppConfig, private authService:AuthenticationService, private logger:LoggingService) { }

  get businessId():string{
    return this.appConfig.businessId;
  }
  get serviceURL():string {
    return this.appConfig.getConfig("propertydataapi_external_url") + '/';
  }
  get optionsJson(){
    return this.appConfig.optionsJson;
  }
  get optionsForm(){
    return this.appConfig.optionsForm;
  }
  
  //#region common data
  public getPropertyTypes():Observable<any>{
    this.logger.log("Getting property types");
    return this.httpClient.get<any>(this.serviceURL+`propertyTypes`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getSaleTypes():Observable<any>{
    this.logger.log("Getting sale types");
    return this.httpClient.get<any>(this.serviceURL+`saleTypes`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateSaleTypeChecklistItems(st:SaleType):Observable<any>{
    this.logger.log("Updating sale type checklist items");
    const dto = SaleType.mapToDTO(st);
    this.logger.log("Updating sale type checklist items:", dto);
    return this.httpClient.put<any>(this.serviceURL+`saleTypeChecklistItems`, dto, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getShowingStatus():Observable<any>{
    this.logger.log("Getting showing status");
    return this.httpClient.get<any>(this.serviceURL+`showingStatus`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuctionAdminDefaults():Observable<any>{
    this.logger.log("Getting auction admin defaults");
    return this.httpClient.get<any>(this.serviceURL+`auctionAdminDefaults`, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region property handling
  public getProperties():Observable<any>{
    this.logger.log("Getting properties");
    return this.httpClient.get<any>(this.serviceURL+`properties/`+ this.businessId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getSoldProperties():Observable<any>{
    this.logger.log("Getting sold properties");
    return this.httpClient.get<any>(this.serviceURL+`soldProperties/`+ this.businessId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyById(propertyId:number):Observable<any>{
    this.logger.log("Getting property Id: " + propertyId.toString());
    return this.httpClient.get<any>(this.serviceURL+`property/`+ propertyId.toString()+'/'+ this.businessId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public saveProperty(property:Property, formData:FormData, imagePool:PropertyImagePoolDTO[], propertyParties:PropertyParty[], propertyTasks:PropertyTask[], propertyPreservation:PropertyPreservation[], propertyManagement:PropertyManagement[]):Observable<any>{
    this.logger.log("Saving property");
    property.businessId=this.businessId;
    const dto=Property.mapToDTO(property);
    dto.imagePool=imagePool;
    dto.addedByUserId=this.authService.currentUserValue!.id;
    dto.editedByUserId=this.authService.currentUserValue!.id;
    const propertyJson=JSON.stringify(dto);
    formData.append('propertyJson',propertyJson);
    if(propertyParties){
      const dto_array = propertyParties.map(pp => PropertyParty.mapToDTO(pp));
      const propertyPartiesJson = JSON.stringify(dto_array);
      formData.append('propertyPartiesJson',propertyPartiesJson);
    }
    if(propertyTasks){
      const dto_array = propertyTasks.map(pt => PropertyTask.mapToDTO(pt));
      const propertyTasksJson = JSON.stringify(dto_array);
      formData.append('propertyTasksJson',propertyTasksJson);
    }
    if(propertyPreservation){
      const dto_array = propertyPreservation.map(pp => PropertyPreservation.mapToDTO(pp));
      const propertyPreservationJson = JSON.stringify(dto_array);
      formData.append('propertyPreservationJson',propertyPreservationJson);
    }
    if(propertyManagement){
      const dto_array = propertyManagement.map(pm => PropertyManagement.mapToDTO(pm));
      const propertyManagementJson = JSON.stringify(dto_array);
      formData.append('propertyManagementJson',propertyManagementJson);
    }
    return this.httpClient.post<any>(this.serviceURL+`saveProperty`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateProperty(property:Property, formData:FormData, imgFilesToRemove:number[], imagePool:PropertyImagePoolDTO[], docFilesToRemove:number[], propertyParties:PropertyParty[], propertyTasks:PropertyTask[], propertyPreservation:PropertyPreservation[], propertyManagement:PropertyManagement[], officialUpdate:boolean):Observable<any>{
    this.logger.log("Updating property");
    const dto=Property.mapToDTO(property);
    dto.removedImages=imgFilesToRemove;
    dto.imagePool=imagePool;
    dto.removedDocuments=docFilesToRemove;
    dto.editedByUserId=this.authService.currentUserValue!.id;
    dto.officialUpdate=officialUpdate;
    const propertyJson=JSON.stringify(dto);
    formData.append('propertyJson',propertyJson);
    if(propertyParties){
      const dto_array = propertyParties.map(pp => PropertyParty.mapToDTO(pp));
      const propertyPartiesJson = JSON.stringify(dto_array);
      formData.append('propertyPartiesJson',propertyPartiesJson);
    }
    if(propertyTasks){
      const dto_array = propertyTasks.map(pt => PropertyTask.mapToDTO(pt));
      const propertyTasksJson = JSON.stringify(dto_array);
      formData.append('propertyTasksJson',propertyTasksJson);
    }
    if(propertyPreservation){
      const dto_array = propertyPreservation.map(pp => PropertyPreservation.mapToDTO(pp));
      const propertyPreservationJson = JSON.stringify(dto_array);
      formData.append('propertyPreservationJson',propertyPreservationJson);
    }
    if(propertyManagement){
      const dto_array = propertyManagement.map(pm => PropertyManagement.mapToDTO(pm));
      const propertyManagementJson = JSON.stringify(dto_array);
      formData.append('propertyManagementJson',propertyManagementJson);
    }
    return this.httpClient.put<any>(this.serviceURL+`updateProperty`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updatePropertyPhase(propertyId:number, propertyPhaseId:number){
     this.logger.log("Updating property phase");
    const request = new UpdatePropertyPhaseRequest();
    request.propertyId = propertyId;
    request.propertyPhaseId = propertyPhaseId;
    request.userId = this.authService.currentUserValue!.id;
    request.businessId = this.businessId;
    return this.httpClient.put<any>(this.serviceURL+`updatePropertyPhase`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public lockProperty(propertyId:number, clientSessionId:string, epochTimeStamp:number){
    this.logger.log("Locking property");
    return this.httpClient.get<any>(this.serviceURL+`lockProperty/`+propertyId.toString() + `/` + clientSessionId + `/` + this.authService.currentUserValue!.id + `/` + this.businessId + '/' + epochTimeStamp, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public unlockProperty(propertyId:number, clientSessionId:string){
    this.logger.log("Unlocking property");
    const request = new UnlockPropertyRequest();
    request.propertyId = propertyId;
    request.clientSessionId = clientSessionId;
    request.userId = this.authService.currentUserValue!.id;
    request.userName = this.authService.currentUserValue!.userName;
    request.businessId = this.businessId;
    //200 (no body): lock released or no lock to release; 409: lock owned by another user => nothing to release, not an error
    return this.httpClient.post<any>(this.serviceURL+`unlockProperty`, request, this.optionsJson).pipe(catchError((error: any):any => {
      if(error?.status === 409) return of(null);
      this.handleError(error);
      return of (this.createBasicResponseError(error));
    }));
  }

  public forceUnlockProperty(propertyId:number, clientSessionId:string){
    this.logger.log("Forcibly unlocking property");
    const request = new UnlockPropertyRequest();
    request.propertyId = propertyId;
    request.clientSessionId = clientSessionId;
    request.userId = this.authService.currentUserValue!.id;
    request.userName = this.authService.currentUserValue!.userName;
    request.businessId = this.businessId;
    return this.httpClient.post<any>(this.serviceURL+`forceUnlockProperty`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteProperty(propertyId:number, physicalDelete:boolean):Observable<any>{
    this.logger.log("Deleting property");
    const request:PropertyDeleteRequest = {id:propertyId, businessId:this.businessId, userId:this.authService.currentUserValue!.id, physicalDelete:physicalDelete};
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteProperty`,  {body: request, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deletePropertyImages(propertyId:number, imageIdArray:number[]):Observable<any>{
    this.logger.log("Updating property images");
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`propertyImages/`+propertyId.toString(), {body: imageIdArray, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deletePropertyDocuments(propertyId:number, documentIdArray:number[]):Observable<any>{
    this.logger.log("Updating property documents");
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`propertyDocuments/`+propertyId.toString(), {body: documentIdArray, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public restoreProperty(propertyId:number):Observable<any>{
    this.logger.log("Restoring property");
    const request:PropertyDeleteRequest = {id:propertyId, businessId:this.businessId, userId:this.authService.currentUserValue!.id, physicalDelete:false};
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.post(this.serviceURL+`restoreProperty`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public finalizeProperty(propertyId:number){
    this.logger.log("Finalizing property");
    return this.httpClient.get<any>(this.serviceURL+`finalizeProperty/`+propertyId.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public registerPropertyView(propertyId:number, userId:string|undefined):Observable<any>{
    this.logger.log("Registering property view");
    return this.httpClient.get<any>(this.serviceURL+`view/`+propertyId.toString() + (userId?'/'+userId:''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyViews(propertyId:number):Observable<any>{
    this.logger.log("Getting property views");
    return this.httpClient.get<any>(this.serviceURL+`propertyViews/`+propertyId.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public registerPropertyAttachmentView(pav:PropertyAttachmentViewRequest):Observable<any>{
    this.logger.log("Registering property attachment view");
    return this.httpClient.post<any>(this.serviceURL+`attachmentView`, pav, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public saveFavoriteProperty(propertyId:number):Observable<any>{
    this.logger.log("Saving favorite property");
    const request:UserPropertyDTO = {propertyId:propertyId, businessId:this.businessId, userId:this.authService.currentUserValue!.id};
    return this.httpClient.post<any>(this.serviceURL+`saveFavorite`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteFavoriteProperty(propertyId:number):Observable<any>{
    this.logger.log("Deleting favorite property");
    const request:UserPropertyDTO = {propertyId:propertyId, businessId:this.businessId, userId:this.authService.currentUserValue!.id};
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteFavorite`, {body: request, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getFavoriteProperties():Observable<any>{
    this.logger.log("Getting favorite properties");
    return this.httpClient.get<any>(this.serviceURL+`favorite/`+this.authService.currentUserValue!.id,  this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public saveCompareProperty(propertyId:number):Observable<any>{
    this.logger.log("Saving compare property");
    const request:UserPropertyDTO = {propertyId:propertyId, businessId:this.businessId, userId:this.authService.currentUserValue!.id};
    return this.httpClient.post<any>(this.serviceURL+`saveCompare`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteCompareProperty(propertyId:number):Observable<any>{
    this.logger.log("Deleting compare property");
    const request:UserPropertyDTO = {propertyId:propertyId, businessId:this.businessId, userId:this.authService.currentUserValue!.id};
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteCompare`, {body: request, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getCompareProperties():Observable<any>{
    this.logger.log("Getting compare properties");
    return this.httpClient.get<any>(this.serviceURL+`compare/`+this.authService.currentUserValue!.id,  this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getNearbyProperties(latitude:number, longitude:number, radius:number):Observable<any>{
    this.logger.log("Getting nearby properties");
    return this.httpClient.get<any>(this.serviceURL+`nearby/`+ this.businessId + '/' + latitude.toString()+'/'+longitude.toString()+'/'+radius.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public checkDuplicateAddress(streetAddress1:string|undefined, streetAddress2: string|undefined, city: string|undefined, state: string|undefined, zipCode: string|undefined, latitude: number|undefined, longitude: number|undefined, radiusMeters: number|undefined, excludePropertyId: number|undefined):Observable<any>{
    this.logger.log("Checking for duplicate addresses");
    const request = new DuplicateAddressCheckRequest();
    request.businessId = this.businessId;
    request.streetAddress1 = streetAddress1;
    request.streetAddress2 = streetAddress2;
    request.city = city;
    request.state = state;
    request.zipCode = zipCode;
    request.latitude = latitude;
    request.longitude = longitude;
    request.radiusMeters = radiusMeters;
    request.excludePropertyId = excludePropertyId;
    return this.httpClient.post<any>(this.serviceURL+`checkDuplicateAddress`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyUpdates(timestamp:Date|undefined){
    this.logger.log("Getting property updates");
    const request:UpdateRequest = {businessId:this.businessId, userId:this.authService.currentUserValue!.id, timestamp:timestamp};
    return this.httpClient.post<any>(this.serviceURL+`updatedProperties`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyAbstracts(propertyId?:number):Observable<any>{
    this.logger.log("Getting property abstracts with id: " + (propertyId?propertyId.toString():'all'));
    return this.httpClient.get<any>(this.serviceURL+`propertyAbstracts/`+ this.authService.currentUserValue!.id + (propertyId?'/'+propertyId:''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyForAdmin(id:number):Observable<any>{
    this.logger.log("Getting property " +id.toString() + " for admin");
    return this.httpClient.get<any>(this.serviceURL+`propertyAdmin/`+ id.toString() +'/'+ this.authService.currentUserValue!.id, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyOffers():Observable<any>{
    this.logger.log("Getting property offers");
    return this.httpClient.get<any>(this.serviceURL+`propertyOffers/`+ this.authService.currentUserValue!.id, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyOrders():Observable<any>{
    this.logger.log("Getting property orders");
    return this.httpClient.get<any>(this.serviceURL+`propertyOrders/`+ this.authService.currentUserValue!.id, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuctionStatusHistory(auctionId:number):Observable<any>{
    this.logger.log("Getting auction status history");
    return this.httpClient.get<any>(this.serviceURL+`auctionStatusHistory/` + auctionId.toString() + '/' + this.authService.currentUserValue!.id, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyScoreboardData(propertyId:number,scheduleSeqNo:number|undefined):Observable<any>{
    this.logger.log("Getting property scoreboard data");
    return this.httpClient.get<any>(this.serviceURL+`propertyScoreboardData/` + this.authService.currentUserValue!.id + '/' + propertyId.toString() + (scheduleSeqNo?'/'+scheduleSeqNo:'') , this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuctionReservePrice(auctionId:number, scheduleSeqNo?:number):Observable<any>{
    return this.httpClient.get<any>(this.serviceURL+`auctionReservePrice/` + auctionId + (scheduleSeqNo?'/'+scheduleSeqNo:''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }

  public hotEdit(he:HotEditRequest){
    this.logger.log("Hot Edit Request");
    return this.httpClient.post<any>(this.serviceURL+`hotEdit`, he, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }

  public reserveCredit(rc:ReserveCreditRequest){
    this.logger.log("Reserve Credit Request");
    return this.httpClient.post<any>(this.serviceURL+`reserveCredit`, rc, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }
  //#endregion
  
  //#region buyer representation
  public representBuyer(brr:BuyerRepresentationRequest){
    brr.userId=this.authService.currentUserValue!.id;
    brr.businessId=this.businessId;
    this.logger.log("Submitting buyer representation request");
    return this.httpClient.post<any>(this.serviceURL+`representBuyer`, brr, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public confirmRepresentation(token:string){
    this.logger.log("Submitting buyer representation confirmation");
    return this.httpClient.post<any>(this.serviceURL+`confirmRepresentation/`+token, { headers: new HttpHeaders().set('Content-Type', 'application/json') }).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region portfolios
  public getPortfolios(){
    this.logger.log("Getting portfolios");
    return this.httpClient.get<any>(this.serviceURL+'portfolios/'+this.authService.currentUserValue!.id + '/' + this.appConfig.businessId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPortfolioById( portfolioId:number){
    this.logger.log("Getting portfolioById");
    return this.httpClient.get<any>(this.serviceURL+'portfolioById/' + portfolioId + '/' + this.appConfig.businessId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public storePortfolio(p:Portfolio):Observable<any>{
    this.logger.log("Storing portfolio");
    p.businessId=this.businessId;
    const request = new StorePortfolioRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.portfolio=Portfolio.mapToDTO(p);
    return this.httpClient.post<any>(this.serviceURL+`storePortfolio`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deletePortfolio(p:Portfolio):Observable<any>{
    this.logger.log("Deleting portfolio");
    const request = new DeletePortfolioRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.portfolioId=p.id!;
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deletePortfolio`, {body: request, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

   public storePortfolioProperty(portfolioId:number|undefined, propertyId:number, reason:string):Observable<any>{
    this.logger.log("Storing portfolio property");
    const request = new StorePortfolioPropertyRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.portfolioId=portfolioId;
    request.propertyId=propertyId;
    request.reason=reason;
    return this.httpClient.post<any>(this.serviceURL+`storePortfolioProperty`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region retrieve data (images,etc) from website bucket as blob
  public downloadData(url:string) {
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  //image of the property image pool (originals are private, served by the api)
  public getPropertyImageFile(propertyId:number, imageId:number) {
    return this.httpClient.get(this.serviceURL+`propertyImageFile/` + propertyId + '/' + imageId, { headers: this.optionsJson.headers, responseType: 'blob' });
  }
  //#endregion

  //#region legal ad
  public createLegalAd(request:CreateLegalAdRequest){
    //this call creates the legal ad for preview
    //needs to be expanded to create legal ad for multiple properties or BETTER: auctions!
    this.logger.log("Creating legal ad");
    request.createdByUserId=this.authService.currentUserValue!.id;
    request.businessId=this.businessId;
    return this.httpClient.post<any>(this.serviceURL+`createLegalAd`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuctionAbstracts(auctionType:number):Observable<any>{
    this.logger.log("Getting auction abstracts");
    return this.httpClient.get<any>(this.serviceURL+`auctionAbstracts/`+ this.authService.currentUserValue!.id + '/' + auctionType.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getAuctionAbstract(auctionId:number):Observable<any>{
    this.logger.log("Getting auction abstract");
    return this.httpClient.get<any>(this.serviceURL+`auctionAbstract/`+ this.authService.currentUserValue!.id + '/' + auctionId.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public saveLegalAd(la:LegalAdRequest){
    //this call stores a new legal ad
    this.logger.log("Saving new legal ad");
    la.createdByUserId=this.authService.currentUserValue!.id;
    la.businessId=this.businessId;
    return this.httpClient.post<any>(this.serviceURL+`saveLegalAd`,la, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateLegalAd(la:LegalAdRequest){
    //this call updates an existing legal ad
    //updating can be for several reasons.Make sure you set the current userId for the correct use case before you call this method
    this.logger.log("Updating existing legal ad");
    la.createdByUserId=this.authService.currentUserValue!.id;
    la.businessId=this.businessId;
    return this.httpClient.put<any>(this.serviceURL+`updateLegalAd`,la, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getLegalAds(includeVoid:boolean){
    //this call fetches all legal ads for the businees
    this.logger.log("Getting all legal ads");
    return this.httpClient.get<any>(this.serviceURL+`legalAds`+ (includeVoid?'/'+includeVoid:''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getLegalAd(legalAdId:number){
    //this call fetches a specific legalAd
    this.logger.log("Getting legal ad");
    return this.httpClient.get<any>(this.serviceURL+`legalAd/`+ legalAdId.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getLegalAdForAuction(auctionId:number){
    //this call fetches a specific legalAd
    this.logger.log("Getting legal ad for auction");
    return this.httpClient.get<any>(this.serviceURL+`legalAdForauction/`+ auctionId.toString(), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public voidLegalAd(lav:LegalAdVoidRequest){
    //this call voids a whole legal ad
    this.logger.log("Voiding whole legal ad with id:" + lav.id);
    lav.userId=this.authService.currentUserValue!.id;
    lav.businessId=this.businessId;
    return this.httpClient.post<any>(this.serviceURL+`voidLegalAd`,lav, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public voidLegalAdForAuction(lav:LegalAdVoidRequest){
    //this call voids a legal ad for one auction
    this.logger.log("Voiding legal ad for auctionId:" + lav.auctionId);
    lav.userId=this.authService.currentUserValue!.id;
    lav.businessId=this.businessId;
    return this.httpClient.post<any>(this.serviceURL+`voidLegalAdForAuction`,lav, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region csv file sampling
  public sampleFile(file:File, fileType:string, fieldDelimiter:string, hasHeader:boolean, startAtRow:number){
    const r = new SampleFileRequest();
    r.userId=this.authService.currentUserValue!.id;
    r.businessId=this.businessId;
    r.type=fileType;
    r.fieldDelimiter=fieldDelimiter;
    r.hasHeader=hasHeader;
    r.startAtRow=startAtRow;
    const body = JSON.stringify(r);
    const formData: FormData = new FormData();
    formData.append('requestString', body);
    formData.append('files', file, file.name);
    this.logger.log("Uploading file to sample ");
    return this.httpClient.post<any>(this.serviceURL+`sampleFile`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region inventory handling
  public storeInventory(i:Inventory):Observable<any>{
    i.businessId=this.businessId;
    const request = new StoreInventoryRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.inventory=Inventory.mapToDTO(i);
    this.logger.log("Store inventory");
    return this.httpClient.post<any>(this.serviceURL+`storeInventory`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public storeInventoryBulk(iArray:Inventory[]):Observable<any>{
    const request = new StoreInventoryBulkRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.inventory= iArray.map(i=>Inventory.mapToDTO(i));
    this.logger.log("Store inventory");
    return this.httpClient.post<any>(this.serviceURL+`storeInventoryBulk`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteInventory(i:Inventory):Observable<any>{
    const request = new DeleteInventoryRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.inventory=Inventory.mapToDTO(i);
    this.logger.log("Delete inventory");
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteInventory`, {body: request, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getInventory():Observable<InventoryResponse>{
    this.logger.log("Getting inventory");
    return this.httpClient.get<InventoryResponse>(this.serviceURL+`inventory/`+ this.businessId, this.optionsJson).pipe(catchError((error: HttpErrorResponse) => {this.handleError(error); return of (Object.assign(new InventoryResponse(), this.createBasicResponseError(error)))}));
  }

  public storeInventoryImages(i:Inventory, images:InventoryImage[]){
    this.logger.log("Storing inventory images");
    const request = new StoreInventoryImagesRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.inventoryId = i.id!;
    request.images = images.map(i=>InventoryImage.mapToDTO(i));
    this.logger.log("Store inventory images");
    return this.httpClient.post<any>(this.serviceURL+`storeInventoryImages`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }

  public getInventoryImages(i:Inventory|number){
    this.logger.log("Getting inventory images");
    let inventoryId:number;
    if(i instanceof Inventory){
      inventoryId=i.id!;
    }
    else inventoryId=i;
    return this.httpClient.get<any>(this.serviceURL+`inventoryImages/` + this.businessId + '/' + inventoryId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region folder structures
  public getFolderStructures(userId:string, clientId:number|undefined, context:string|undefined, contextId:number|undefined){
    this.logger.log("Getting folder structures");
    const params = new HttpParams()
      .set('UserId',userId)
      .set('BusinessId',this.appConfig.businessId)
      .set('ClientId',clientId?clientId:'')
      .set('Context',context?context:'')
      .set('ContextId',contextId?contextId:'')
    return this.httpClient.get<any>(this.serviceURL+'folderStructures', {headers:this.optionsJson.headers, params: params}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }


  public storeFolderStructure(fs:FolderStructure,userId:string):Observable<any>{
    this.logger.log("Storing folder structure");
    fs.businessId = this.businessId;
    const request = new StoreFolderStructureRequest();
    request.businessId=this.businessId;
    request.userId=userId;
    request.folderStructure=FolderStructure.mapToDTO(fs);
    return this.httpClient.post<any>(this.serviceURL+`storeFolderStructure`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region vault documents
  public getVaultDocuments(userId:string, clientId:number|undefined, context:string|undefined, contextId:number|undefined, subContextId:number|undefined, restrictedToUserId:string|undefined, privateOnly:boolean|undefined){
    this.logger.log("Getting vault documents");
    const params = new HttpParams()
      .set('UserId',userId)
      .set('BusinessId',this.appConfig.businessId)
      .set('ClientId',clientId?clientId:'')
      .set('Context',context?context:'')
      .set('ContextId',contextId?contextId:'')
      .set('SubContextId',subContextId?subContextId:'')
      .set('RestrictedToUserId',restrictedToUserId?restrictedToUserId:'')
      .set('PrivateOnly', privateOnly?privateOnly:'')
    return this.httpClient.get<any>(this.serviceURL+'vaultDocuments', {headers:this.optionsJson.headers, params: params}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getVaultDocumentsWithFile(userId:string, clientId:number|undefined, context:string|undefined, contextId:number|undefined, subContextId:number|undefined, restrictedToUserId:string|undefined, privateOnly:boolean|undefined){
    this.logger.log("Getting vault documents with file");
    const params = new HttpParams()
      .set('UserId',userId)
      .set('BusinessId',this.appConfig.businessId)
      .set('ClientId',clientId?clientId:'')
      .set('Context',context?context:'')
      .set('ContextId',contextId?contextId:'')
      .set('SubContextId',subContextId?subContextId:'')
      .set('RestrictedToUserId',restrictedToUserId?restrictedToUserId:'')
      .set('PrivateOnly', privateOnly?privateOnly:'')
    return this.httpClient.get<any>(this.serviceURL+'vaultDocumentsWithFile', {headers:this.optionsJson.headers, params: params}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public storeVaultDocument(vd:VaultDocument,userId:string):Observable<any>{
    this.logger.log("Storing vault document");
    vd.businessId = this.businessId;
    const formData = new FormData();
    if(vd.file) formData.append('files', vd.file, vd.file.name);
    const request = new StoreVaultDocumentRequest();
    request.businessId=this.businessId;
    request.userId=userId;
    request.vaultDocument=VaultDocument.mapToDTO(vd);
    const requestJson=JSON.stringify(request);
    formData.append('requestJson',requestJson);
    return this.httpClient.post<any>(this.serviceURL+`storeVaultDocument`, formData, this.optionsForm).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  //#region trash
  public getPropertyTrash(propertyId:number):Observable<any>{
    return this.httpClient.get<any>(this.serviceURL+`propertyTrash/`+propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public restoreFromTrash(propertyId:number, kind:TrashItemKind, id:number):Observable<any>{
    const url = kind === 'image' ? `restorePropertyImage` : `restoreVaultDocument`;
    return this.httpClient.post<any>(this.serviceURL+url, { propertyId, kind, id }, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deleteFromTrash(propertyId:number, kind:TrashItemKind, id:number):Observable<any>{
    return this.httpClient.request<any>('delete', this.serviceURL+`trashItem`, {body: { propertyId, kind, id }, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  public deleteVaultDocument(vd:VaultDocument,userId:string):Observable<any>{
    this.logger.log("Deleting vault document");
    vd.businessId = this.businessId;
    const request = new DeleteVaultDocumentRequest();
    request.businessId=this.businessId;
    request.userId=userId;
    request.vaultDocument=VaultDocument.mapToDTO(vd);
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deleteVaultDocument`, {body: request, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public updateVaultDocumentFolderPath(idList:Array<number>,folderStructureId:number,folderStructurePath:string):Observable<any>{
    this.logger.log("Updating vault document folder path");
    const request = new UpdateVaultDocumentFolderPathRequest();
    request.idList=idList;
    request.folderStructureId=folderStructureId;
    request.folderStructurePath=folderStructurePath;
    return this.httpClient.post<any>(this.serviceURL+`updateVaultDocumentFolderPath`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getVaultFile(id:number, userId:string, propertyId?:number){
    this.logger.log("Getting vault file");
    return this.httpClient.get<any>(this.serviceURL+'vaultFile/'+userId + '/' + this.appConfig.businessId + '/' + id + (propertyId ? '?propertyId=' + propertyId : ''), this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion
  
  //#region nda
  public draftNDAForAuction(request:DraftLegalDocumentRequest){
    request.userId=this.authService.currentUserValue!.id;
    return this.httpClient.post<any>(this.serviceURL+`draftNDAForAuction`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));

  }

  public executeNDAForAuction(request:ExecuteLegalDocumentRequest):Observable<any>{
    request.userId=this.authService.currentUserValue!.id;
    return this.httpClient.post<any>(this.serviceURL+`executeNDAForAuction`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region buyer agreement
  public draftBuyerAgreementForAuction(request:DraftLegalDocumentRequest){
    request.userId=this.authService.currentUserValue!.id;
    return this.httpClient.post<any>(this.serviceURL+`draftBuyerAgreementForAuction`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public executeBuyerAgreementForAuction(request:ExecuteLegalDocumentRequest):Observable<any>{
    request.userId=this.authService.currentUserValue!.id;
    return this.httpClient.post<any>(this.serviceURL+`executeBuyerAgreementForAuction`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region auction authorization status
  public userAuctionAuthorizationStatus(request:UserAuctionAuthorizationStatusRequest):Observable<any>{
    request.userId=this.authService.currentUserValue!.id;
    request.businessId=this.businessId;
    return this.httpClient.post<any>(this.serviceURL+`userAuctionAuthorizationStatus`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region property notes
  public getPropertyNotes(propertyId:number){
    this.logger.log("Getting property notes");
    return this.httpClient.get<any>(this.serviceURL+'propertyNotes/'+this.authService.currentUserValue!.id + '/' + this.appConfig.businessId + '/' + propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public storePropertyNote(pn:PropertyNote):Observable<any>{
    this.logger.log("Storing property note");
    pn.userId = this.authService.currentUserValue!.id;
    const request = new StorePropertyNoteRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.propertyNote=PropertyNote.mapToDTO(pn);
    return this.httpClient.post<any>(this.serviceURL+`storePropertyNote`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deletePropertyNote(pn:PropertyNote):Observable<any>{
    this.logger.log("Deleting property note");
    const request = new DeletePropertyNoteRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.propertyNoteId=pn.id!;
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deletePropertyNote`, {body: request, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region property expenses
  public getPropertyExpenses(propertyId:number){
    this.logger.log("Getting property expenses");
    return this.httpClient.get<any>(this.serviceURL+'propertyExpenses/'+this.authService.currentUserValue!.id + '/' + this.appConfig.businessId + '/' + propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public storePropertyExpense(pe:PropertyExpense):Observable<any>{
    this.logger.log("Storing property expense");
    pe.userId = this.authService.currentUserValue!.id;
    const request = new StorePropertyExpenseRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.propertyExpense=PropertyExpense.mapToDTO(pe);
    return this.httpClient.post<any>(this.serviceURL+`storePropertyExpense`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public deletePropertyExpense(pe:PropertyExpense):Observable<any>{
    this.logger.log("Deleting property expense");
    const request = new DeletePropertyExpenseRequest();
    request.businessId=this.businessId;
    request.userId=this.authService.currentUserValue!.id;
    request.propertyExpenseId=pe.id!;
    //at the time of coding this httpClient.delete does not allow for a body. using universal request instead.
    return this.httpClient.request<any>('delete', this.serviceURL+`deletePropertyExpense`, {body: request, headers: this.optionsJson.headers}).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region property parties
  public getPropertyParties(propertyId:number){
    this.logger.log("Getting property parties");
    return this.httpClient.get<any>(this.serviceURL+'propertyParties/'+propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region property tasks
  public getPropertyTasks(propertyId:number){
    this.logger.log("Getting property tasks");
    return this.httpClient.get<any>(this.serviceURL+'propertyTasks/'+propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region property preservation
  public getPropertyPreservation(propertyId:number){
    this.logger.log("Getting property preservation");
    return this.httpClient.get<any>(this.serviceURL+'propertyPreservation/'+propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region property management
  public getPropertyManagement(propertyId:number){
    this.logger.log("Getting property management");
    return this.httpClient.get<any>(this.serviceURL+'propertyManagement/'+propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion

  //#region property master settings
  public getPropertyMasterCatalog(){
    this.logger.log("Getting property master catalog");
    return this.httpClient.get<any>(this.serviceURL+'propertyMasterCatalog', this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyVisibility(propertyId:number){
    return this.httpClient.get<any>(this.serviceURL+'propertyVisibility/'+propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyVisibilityMatrix(propertyId:number){
    return this.httpClient.get<any>(this.serviceURL+'propertyVisibilityMatrix/'+propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  /** visible null: back to the default of the catalog */
  public storePropertyVisibility(request:{propertyId:number, audience:number, itemKey:string, visible:boolean|null}):Observable<any>{
    return this.httpClient.post<any>(this.serviceURL+'storePropertyVisibility', request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public getPropertyMasterSettings(propertyId:number){
    this.logger.log("Getting property master settings for property " + propertyId);
    return this.httpClient.get<any>(this.serviceURL+'propertyMasterSettings/'+ this.appConfig.businessId + '/' + propertyId, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }

  public storePropertyMasterSettings(pms:PropertyMasterSettings):Observable<any>{
    this.logger.log("Storing property master settings");
    const request=PropertyMasterSettings.mapToDTO(pms);
    return this.httpClient.post<any>(this.serviceURL+`storePropertyMasterSettings`, request, this.optionsJson).pipe(catchError((error: any):any => {this.handleError(error); return of (this.createBasicResponseError(error))}));
  }
  //#endregion


  private createBasicResponseError(error: HttpErrorResponse){
    const br = new BasicResponse();
    br.success=false;
    br.message='Backend error - please try again later';
    return br;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
        // A client-side or network error occurred. Handle it accordingly.
        console.error('An error occurred:', error.error.message);
    } else {
        // The backend returned an unsuccessful response code.
        // The response body may contain clues as to what went wrong,
        console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(()=>(error));
  }
}
